import api from './api';

// Definir interfaces
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface PermisosBackend {
  p_busqueda?: boolean | number;
  p_carga?: boolean | number;
  p_modif_analisis?: boolean | number;
  p_nuevo_analisis?: boolean | number;
  p_pendientes?: number;
  p_g_alertas?: boolean | number;
  p_g_config?: boolean | number;
  p_g_ensayos?: boolean | number;
  p_g_usuarios?: boolean | number;
}

export interface UserPermissions {
  p_busqueda?: boolean | number;
  p_carga?: boolean | number;
  p_edicion?: boolean | number;
  p_eliminar?: boolean | number;
  p_admin?: boolean | number;
  p_nuevo_analisis?: boolean | number;
}

export interface UserInfo {
  id: number;
  username: string;
  nombre: string;
  apellido: string;
  email: string;
  tipo?: string;
  tipo_id?: number;
  tabla_grupo?: string;
  id_grupo?: number;
  info_adicional?: any;
  permisos?: PermisosBackend;
  estado_pass?: number;
  estado?: number;
  p_busqueda?: boolean | number;
  p_carga?: boolean | number;
  p_edicion?: boolean | number;
  p_eliminar?: boolean | number;
  p_admin?: boolean | number;
  p_nuevo_analisis?: boolean | number;
}

export interface LoginResponse {
  user: UserInfo;
  token: string;
}

export interface UserProfile {
  id: number;
  nom_usuario: string;
  nombre: string;
  apellido: string;
  email: string;
  estado: number;
  estado_pass: number;
  tabla_grupo: string;
  id_grupo: number;
  notificaciones: number;
  info_adicional?: any;
  permisos?: PermisosBackend;
  tipo_usuario?: string;
}

// Función para realizar solicitudes autenticadas
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No hay token de autenticación');
  }
  
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
  
  return fetch(url, {
    ...options,
    headers
  });
};

// Función para iniciar sesión
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await api.post('/auth/login', credentials);
  
  // Guardar el token en localStorage
  if (response.data.token) {
    const user = response.data.user as UserInfo;
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(user));
  }
  
  return response.data;
};

// Función para cerrar sesión
export const logout = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Función para obtener el perfil del usuario
export const getUserProfile = async (): Promise<UserProfile> => {
  // Configurar el encabezado de autorización
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No hay token de autenticación');
  }
  
  const config = {
    headers: { Authorization: `Bearer ${token}` }
  };
  
  const response = await api.get('/auth/profile', config);
  return response.data;
};

// Función para verificar si el usuario está autenticado
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('token');
  return !!token;
};

// Función para obtener el usuario actual desde localStorage
export const getCurrentUser = (): UserInfo | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    return JSON.parse(userStr);
  }
  return null;
};

// Función para verificar el tipo de usuario
export const getUserType = (): string | null => {
  const user = getCurrentUser();
  
  if (!user) {
    return null;
  }
  
  // Si tiene tabla_grupo, usarla para determinar el tipo
  if (user.tabla_grupo) {
    const tipoDesdeTabla = user.tabla_grupo === 'administradores' 
      ? 'administrador' 
      : user.tabla_grupo === 'clientes'
        ? 'cliente'
        : user.tabla_grupo === 'laboratorios'
          ? 'laboratorio'
          : null;
          
    // Si se pudo determinar un tipo desde tabla_grupo, usarlo
    if (tipoDesdeTabla) {
      return tipoDesdeTabla;
    }
  }
  
  // Si llegamos aquí, verificar el campo tipo
  if (user.tipo) {
    // Normalizar el tipo de usuario (singular)
    if (user.tipo === 'administradores' || user.tipo === 'admin') {
      return 'administrador';
    }
    
    if (user.tipo === 'clientes') {
      return 'cliente';
    }
    
    if (user.tipo === 'laboratorios') {
      return 'laboratorio';
    }
    
    return user.tipo;
  }
  
  return null;
};

// ------- Permisos -------
//
// Los permisos viven en la ficha de la organización (clientes o laboratorios) o
// en la del administrador, y el backend los envía ya normalizados en `permisos`
// al iniciar sesión. Se mantiene la lectura de `info_adicional` como respaldo
// para sesiones abiertas antes de esta versión.

type PermisoBackend =
  | 'p_busqueda'
  | 'p_carga'
  | 'p_modif_analisis'
  | 'p_nuevo_analisis'
  | 'p_g_alertas'
  | 'p_g_config'
  | 'p_g_ensayos'
  | 'p_g_usuarios';

const toBoolean = (value: unknown): boolean =>
  value === true || value === 1 || value === '1' || value === 'true';

const rawPermission = (permiso: PermisoBackend): boolean => {
  const user = getCurrentUser();
  if (!user) return false;
  const fuente = user.permisos ?? user.info_adicional;
  if (!fuente) return false;
  return toBoolean((fuente as Record<string, unknown>)[permiso]);
};

// Ningún tipo de usuario tiene permisos implícitos: un administrador sin
// permisos marcados no ve ninguna acción operativa.
export const getUserPermissions = (): UserPermissions | null => {
  if (!getCurrentUser()) return null;
  const esAdministrador = getUserType() === 'administrador';

  return {
    p_busqueda: rawPermission('p_busqueda'),
    p_carga: rawPermission('p_carga'),
    p_edicion: rawPermission('p_modif_analisis'),
    p_eliminar: esAdministrador && rawPermission('p_modif_analisis'),
    p_admin: esAdministrador && rawPermission('p_g_usuarios'),
    p_nuevo_analisis: rawPermission('p_nuevo_analisis'),
  };
};

export const hasPermission = (permission: keyof UserPermissions): boolean => {
  const permisos = getUserPermissions();
  return permisos ? !!permisos[permission] : false;
};

// Los permisos de gestión son exclusivos de los administradores y el servidor
// los exige en cada pedido.
const managementPermission = (permiso: PermisoBackend): boolean =>
  getUserType() === 'administrador' && rawPermission(permiso);

export const hasUserManagementPermission = (): boolean => managementPermission('p_g_usuarios');

export const hasTestManagementPermission = (): boolean => managementPermission('p_g_ensayos');

export const hasConfigManagementPermission = (): boolean => managementPermission('p_g_config');

export const hasAlertManagementPermission = (): boolean => managementPermission('p_g_alertas');

// La gestión de configuración ya no tiene pantalla propia, así que no habilita
// por sí sola el acceso a Gestión.
export const hasAnyManagementPermission = (): boolean =>
  hasUserManagementPermission() || hasTestManagementPermission();

// Vuelve a leer el perfil del servidor y actualiza la copia local. Así un cambio
// de permisos se aplica al recargar la página, sin volver a iniciar sesión.
export const refreshCurrentUser = async (): Promise<UserInfo | null> => {
  const user = getCurrentUser();
  if (!user) return null;

  try {
    const perfil = await getUserProfile();
    const actualizado: UserInfo = {
      ...user,
      nombre: perfil.nombre,
      apellido: perfil.apellido,
      email: perfil.email,
      estado: perfil.estado,
      estado_pass: perfil.estado_pass,
      tabla_grupo: perfil.tabla_grupo,
      id_grupo: perfil.id_grupo,
      info_adicional: perfil.info_adicional,
      permisos: perfil.permisos,
    };
    localStorage.setItem('user', JSON.stringify(actualizado));
    return actualizado;
  } catch {
    // Sin conexión o sesión vencida: se sigue con la copia local.
    return user;
  }
};
